import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import type { Item } from "../../api";
import { ErrorNote, KindTag, Loading, PriorityChip, StatusChip, timeAgo, useApi } from "../../components/ui";
import { usePickers } from "./shared";

const VIEWS = [
  ["queue", "Work queue"],
  ["triage", "Needs triage"],
  ["unsorted", "Unsorted"],
  ["mine", "Assigned to me"],
  ["all", "Everything"],
] as const;

const PAGE = 50;

export function StaffQueue() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const view = params.get("view") ?? "queue";
  const [q, setQ] = useState(params.get("q") ?? "");
  const pickers = usePickers();

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "offset") next.delete("offset");
    navigate(`/staff/queue?${next.toString()}`, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") ?? "") !== q) set("q", q || null);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const apiParams = new URLSearchParams(search);
  if (!apiParams.get("view")) apiParams.set("view", "queue");
  apiParams.set("limit", String(PAGE));
  const { data, error, loading } = useApi<{ items: Item[]; total: number; offset: number }>(`/items?${apiParams.toString()}`);
  const offset = Number(params.get("offset") ?? 0);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Queue</h1>
        <Link href="/staff/new" className="btn btn-primary">
          New task or ticket
        </Link>
      </div>
      <div className="tabs" role="tablist">
        {VIEWS.map(([v, label]) => (
          <button key={v} role="tab" aria-selected={view === v} className={view === v ? "active" : ""} onClick={() => set("view", v)} data-tour={`queue-tab-${v}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="filters">
        <label className="sr-only" htmlFor="q">Search</label>
        <input id="q" type="search" placeholder="Search or DL-1234" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 220 }} />
        <Select label="Kind" value={params.get("kind")} onChange={(v) => set("kind", v)} options={[["ticket", "Tickets"], ["feedback", "In-app feedback"], ["task", "Tasks"], ["github_issue", "GitHub issues"]]} />
        <Select label="Priority" value={params.get("priority")} onChange={(v) => set("priority", v)} options={[["0", "Urgent"], ["1", "High"], ["2", "Normal"], ["3", "Low"], ["none", "Untriaged"]]} />
        {view !== "queue" && view !== "triage" && (
          <Select label="Status" value={params.get("status")} onChange={(v) => set("status", v)} options={[["active", "Open (any)"], ["open", "Open"], ["in_progress", "In progress"], ["waiting", "Waiting"], ["resolved", "Resolved"], ["closed", "Closed"], ["any", "Any status"]]} />
        )}
        <Select label="Customer" value={params.get("org")} onChange={(v) => set("org", v)} options={pickers.orgs.map((o) => [o.id, o.name])} />
        <Select label="Project" value={params.get("project")} onChange={(v) => set("project", v)} options={pickers.projects.map((p) => [p.id, p.name])} />
        {view !== "mine" && <Select label="Assignee" value={params.get("assignee")} onChange={(v) => set("assignee", v)} options={[["none", "Unassigned"], ...pickers.users.map((u) => [u.id, u.name] as [string, string])]} />}
        <Select label="Sort" value={params.get("sort")} onChange={(v) => set("sort", v)} options={[["updated", "Recently updated"], ["created", "Newest"]]} placeholder="Priority score" />
        {params.get("repo") && (
          <button className="btn btn-small" onClick={() => set("repo", null)}>
            Repo: {params.get("repo")} ✕
          </button>
        )}
      </div>
      <ErrorNote message={error} />
      {loading && !data ? (
        <Loading />
      ) : (
        <div className="card table-wrap" style={{ padding: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Customer</th>
                <th>Assignee</th>
                <th>Updated</th>
                <th className="num" title="Queue score">Score</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ textAlign: "center", padding: 32 }}>
                    Nothing matches. {view === "triage" ? "Everything's triaged." : view === "unsorted" ? "Every inbound request has a customer." : ""}
                  </td>
                </tr>
              )}
              {data?.items.map((i) => (
                <tr key={i.id} className="clickable" onClick={(e) => !(e.target as HTMLElement).closest("a") && navigate(`/staff/items/${i.number}`)}>
                  <td className="mono small">{i.ref}</td>
                  <td className="title-cell">
                    <Link href={`/staff/items/${i.number}`}>{i.title}</Link>
                    <div>
                      <KindTag kind={i.kind} />
                      {i.githubRepo && <span className="small muted"> · {i.githubRepo}#{i.githubNumber}</span>}
                      {i.projectName && <span className="small muted"> · {i.projectName}</span>}
                      {i.effort ? <span className="small muted"> · effort {i.effort}</span> : null}
                    </div>
                  </td>
                  <td>
                    <PriorityChip priority={i.priority} />
                  </td>
                  <td>
                    <StatusChip status={i.status} />
                  </td>
                  <td className="small">{i.needsSorting ? <span className="chip chip-open">Unsorted</span> : (i.orgName ?? <span className="muted">Internal</span>)}</td>
                  <td className="small">{i.assigneeName ?? <span className="muted">—</span>}</td>
                  <td className="small muted">{timeAgo(i.updatedAt)}</td>
                  <td className="num mono small">{i.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && data.total > PAGE && (
            <div className="pager">
              <span className="muted">
                {offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}
              </span>
              <div className="row">
                <button className="btn btn-small" disabled={offset === 0} onClick={() => set("offset", String(Math.max(0, offset - PAGE)))}>
                  Previous
                </button>
                <button className="btn btn-small" disabled={offset + PAGE >= data.total} onClick={() => set("offset", String(offset + PAGE))}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }: { label: string; value: string | null; onChange: (v: string | null) => void; options: Array<readonly [string, string]>; placeholder?: string }) {
  return (
    <>
      <label className="sr-only" htmlFor={`f-${label}`}>
        {label}
      </label>
      <select id={`f-${label}`} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{placeholder ?? `${label}: any`}</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </>
  );
}
